import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { useGetFolderList } from '@/modules/gpt-chats/hooks/use-kb';

import { Skeleton } from '@/components/ui-kit/skeleton';
import { Search, Folder } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@/components/ui-kit/card';
import { IFolder } from '@/modules/gpt-chats/types/kb.service.type';

interface AgentConfigAddKBModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentKbIds: string[];
  onAddKB: (id: string) => void;
  onRemoveKB: (id: string) => void;
}

export const AgentConfigAddKBModal = ({
  open,
  onOpenChange,
  currentKbIds,
  onAddKB,
  onRemoveKB,
}: AgentConfigAddKBModalProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');


  const { data: folderlistData, isLoading } = useGetFolderList();
  const folders = folderlistData?.data?.folders || [];

  const filteredFolders = folders.filter((folder: IFolder) =>
    (folder.Name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClose = () => {
    setSearchQuery('');
    onOpenChange(false);
  };

  const selectedCount = folders.filter((folder: IFolder) =>
    currentKbIds.includes(folder._id)
  ).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[800px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">{t('ADD_KB_FOLDER')}</DialogTitle>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('ADD_KB_FOLDER_DESCRIPTION')}
          </p>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('SEARCH_FOLDERS_PLACEHOLDER')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="h-[400px] space-y-2 overflow-y-auto">
            {isLoading ? (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-48" />
                        </div>
                      </div>
                      <Skeleton className="h-6 w-10" />
                    </div>
                  </Card>
                ))}
              </>
            ) : filteredFolders.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <Folder className="mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? t('NO_FOLDERS_FOUND_MATCHING_SEARCH') : t('NO_FOLDERS_AVAILABLE')}
                </p>
              </div>
            ) : (
              filteredFolders.map((folder: IFolder) => {
                const isSelected = currentKbIds.includes(folder._id);

                return (
                  <Card key={folder._id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
                          <Folder className="h-5 w-5" />
                        </div>
                         <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-high-emphasis">
                            {folder.Name || t('UNNAMED_FOLDER')}
                          </div>
                           <div className="line-clamp-2 break-all text-sm text-muted-foreground">
                            {folder.Description || t('NO_DESCRIPTION')}
                          </div>
                        </div>
                      </div>

                      {isSelected ? (
                        <Button
                          variant="outline"
                           size="sm"
                          onClick={() => onRemoveKB(folder._id)}
                          className="flex-shrink-0"
                        >
                          {t('REMOVE')}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                           size="sm"
                          onClick={() => onAddKB(folder._id)}
                          className="flex-shrink-0"
                        >
                          {t('ADD')}
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })
            )}
          </div>

           <div className="h-5">
            <div className="text-sm text-muted-foreground">
              {t('FOLDERS_SELECTED_COUNT', { count: selectedCount })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
